import Link from "next/link";

import { ObfuscatedEmail } from "@/components/legal/obfuscated-email";

/**
 * Slim footer that surfaces the legal pages from any layout.
 *
 * Used in:
 * - the (public) layout below the legal-document body
 * - the post-login platform shell, sitting at the bottom of the
 *   authenticated chrome
 *
 * The Imprint link points externally to the marketing site at
 * www.fintum-mi.com/imprint — the application portal does not host
 * its own Impressum (§ 5 TMG is satisfied at the marketing site,
 * which is the canonical Telemedien surface). The DSB / DPO contact
 * is rendered through ``<ObfuscatedEmail>`` so the bare ``mailto:``
 * never appears in the static HTML for scrapers.
 */
export function LegalFooter({
  variant = "default",
}: {
  /** ``compact`` collapses the copyright onto the same row as the
   *  link list; used inside the platform shell where vertical space
   *  is at a premium. ``default`` stacks them with more breathing
   *  room and a slightly larger font size; used in the (public)
   *  legal-page layout. */
  variant?: "default" | "compact";
}) {
  const compact = variant === "compact";
  return (
    <footer
      className={`border-t border-border ${compact ? "py-3" : "py-6"} text-text-tertiary`}
    >
      <div
        className={`mx-auto flex flex-wrap items-center ${compact ? "gap-x-4 gap-y-1 text-xs" : "gap-x-6 gap-y-2 text-sm"} max-w-4xl px-6`}
      >
        <a
          href="https://www.fintum-mi.com/imprint"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-text-primary"
        >
          Imprint
        </a>
        <Link href="/legal/privacy/v1" className="hover:text-text-primary">
          Privacy
        </Link>
        <Link href="/legal/terms/v1" className="hover:text-text-primary">
          Terms
        </Link>
        <Link href="/legal/subprocessors/v1" className="hover:text-text-primary">
          Subprocessors
        </Link>
        <Link href="/legal/security/v1" className="hover:text-text-primary">
          Security
        </Link>
        <Link href="/legal/changelog" className="hover:text-text-primary">
          Changelog
        </Link>
        <ObfuscatedEmail
          user="dpo"
          domain="fintum-mi.com"
          label="DSB"
          className="hover:text-text-primary"
        />
        <span className={compact ? "ml-auto" : "basis-full sm:ml-auto sm:basis-auto"}>
          © {new Date().getFullYear()} Fintum Market Intelligence GmbH
        </span>
      </div>
    </footer>
  );
}
